import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText, Plus, IndianRupee, TrendingUp, Clock, CheckCircle2,
  X, Search, Trash2, RefreshCw, ChevronDown, Printer, Ban,
  CreditCard, ChevronRight, Loader2, AlertCircle, Minus,
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { clsx } from 'clsx';
import { useAuthStore } from '@/store/auth.store';
import { useBranding } from '@/shared/hooks/useBranding';
import PrintableInvoice, { InvoicePaperSize } from '../components/PrintableInvoice';

const BILLING_WRITE_ROLES = new Set(['hospital_admin', 'owner', 'billing_staff']);

const STATUS_STYLE: Record<string, string> = {
  draft:          'bg-gray-100 text-gray-600',
  final:          'bg-blue-100 text-blue-700',
  paid:           'bg-green-100 text-green-700',
  partially_paid: 'bg-yellow-100 text-yellow-700',
  cancelled:      'bg-red-100 text-red-500',
};

const BILLING_TYPE_LABEL: Record<string, string> = {
  cash: 'Cash', tpa: 'TPA', pmjay: 'PM-JAY',
  cghs: 'CGHS', echs: 'ECHS', esi: 'ESI',
};

// billing_master category → invoice line category
const BM_TO_LINE_CAT: Record<string, string> = {
  CONSULTATION: 'consultation',
  BED:          'room_charge',
  PATHOLOGY:    'lab',
  RADIOLOGY:    'radiology',
  PROCEDURE:    'procedure',
  PHARMACY:     'pharmacy',
  OTHER:        'other',
};

function KpiCard({ label, value, sub, icon: Icon, accent }: any) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-card">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-400">{label}</span>
        <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', accent)}>
          <Icon size={16}/>
        </div>
      </div>
      <div className="text-2xl font-bold text-navy">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

function fmt(n: any) {
  return '₹' + Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

// ── Line item type & helpers ──────────────────────────────────────

interface LineItem {
  description: string; category: string; hsn_sac_code: string;
  quantity: number; unit_rate: number; discount_pct: number;
  gst_rate: number; is_exempt: boolean;
}

const BLANK_LINE: LineItem = {
  description: '', category: 'consultation', hsn_sac_code: '',
  quantity: 1, unit_rate: 0, discount_pct: 0, gst_rate: 0, is_exempt: true,
};

const CATEGORY_OPTIONS = [
  'consultation','procedure','pharmacy','lab','radiology',
  'room_charge','nursing','diet','ambulance','other',
];

const GST_RATES = [0, 5, 12, 18];

function computeLine(item: LineItem) {
  const base    = item.quantity * item.unit_rate;
  const discAmt = base * item.discount_pct / 100;
  const taxable = base - discAmt;
  const cgst    = item.is_exempt ? 0 : +(taxable * item.gst_rate / 2 / 100).toFixed(2);
  const sgst    = item.is_exempt ? 0 : +(taxable * item.gst_rate / 2 / 100).toFixed(2);
  return { base, discAmt, taxable, cgst, sgst, lineTotal: +(taxable + cgst + sgst).toFixed(2) };
}

// ── Patient Autocomplete ──────────────────────────────────────────

function PatientAutocomplete({
  onSelect,
}: {
  onSelect: (p: any) => void;
}) {
  const [query,      setQuery]      = useState('');
  const [open,       setOpen]       = useState(false);
  const [results,    setResults]    = useState<any[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [dropPos,    setDropPos]    = useState({ top: 0, left: 0, width: 0 });
  const inputRef  = useRef<HTMLInputElement>(null);
  const debounce  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const doSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await api.get('/patients', { params: { search: q.trim(), limit: 8 } });
      // API returns { data: [...], total: n }
      setResults(res.data.data ?? []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setQuery(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => doSearch(v), 300);
  }

  function handleFocus() {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    setOpen(true);
    if (query.trim().length >= 2 && !results.length) doSearch(query);
  }

  function handleBlur() {
    // Delay so mouse-click on option registers before close
    setTimeout(() => setOpen(false), 160);
  }

  function pick(p: any) {
    onSelect(p);
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  const showDrop = open && (loading || results.length > 0 || query.trim().length >= 2);

  return (
    <>
      <div ref={wrapperRef}
        className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 focus-within:border-navy transition-colors">
        <Search size={13} className="text-gray-400 flex-shrink-0"/>
        <input
          ref={inputRef}
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Search by name, mobile, or UID…"
          className="flex-1 text-sm outline-none bg-transparent"
          autoComplete="off"
        />
        {loading && <RefreshCw size={12} className="animate-spin text-gray-400 flex-shrink-0"/>}
      </div>

      {/* Dropdown rendered at fixed position outside modal scroll */}
      {showDrop && (
        <div
          style={{
            position: 'fixed',
            top:   dropPos.top,
            left:  dropPos.left,
            width: dropPos.width,
            zIndex: 200,
          }}
          className="bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden max-h-56 overflow-y-auto"
        >
          {loading && !results.length && (
            <div className="px-4 py-3 text-xs text-gray-400 flex items-center gap-2">
              <RefreshCw size={11} className="animate-spin"/> Searching…
            </div>
          )}
          {!loading && results.length === 0 && query.trim().length >= 2 && (
            <div className="px-4 py-3 text-xs text-gray-400">No patients found for "{query}"</div>
          )}
          {results.map((p: any) => (
            <button
              key={p.id}
              onMouseDown={() => pick(p)}
              className="w-full text-left px-4 py-2.5 hover:bg-navy/4 transition flex items-center justify-between gap-3 border-b border-gray-50 last:border-0"
            >
              <div>
                <div className="font-medium text-gray-800 text-sm">{p.name}</div>
                <div className="text-xs text-gray-400">
                  {p.patient_uid}
                  {p.age_years ? ` · ${p.age_years}y` : ''}
                  {p.gender    ? ` · ${p.gender === 'male' ? 'M' : p.gender === 'female' ? 'F' : 'O'}` : ''}
                </div>
              </div>
              <span className="text-xs text-navy flex-shrink-0">Select →</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ── Line Item Description Combo ───────────────────────────────────

interface MasterItem {
  id: string; service_name: string; category: string;
  price: number; tax_pct: number; doctor_name: string | null;
}

// Self-contained autocomplete — manages its own open/close, keyboard navigation,
// and fixed-position dropdown portal so it is never clipped by overflow-hidden ancestors.
function DescriptionCombo({
  value,
  lineIndex,
  masterItems,
  onChange,
  onSelect,
}: {
  value:       string;
  lineIndex:   number;
  masterItems: MasterItem[];
  onChange:    (v: string) => void;
  onSelect:    (item: MasterItem) => void;
}) {
  const [open,    setOpen]    = useState(false);
  const [hiIdx,   setHiIdx]   = useState(-1);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);

  const suggestions = useMemo(() => {
    if (!value.trim()) return masterItems.slice(0, 15);
    const q = value.toLowerCase();
    return masterItems.filter(m => m.service_name.toLowerCase().includes(q)).slice(0, 15);
  }, [value, masterItems]);

  function openDrop() {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setDropPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 280) });
    setOpen(true);
  }

  function closeDrop() {
    setOpen(false);
    setHiIdx(-1);
  }

  function handleSelect(item: MasterItem) {
    onSelect(item);
    closeDrop();
    // After selection, move focus to the QTY cell so the clerk can tab through numerics
    requestAnimationFrame(() => {
      const qtyInput = document.querySelector<HTMLInputElement>(`[data-line-qty="${lineIndex}"]`);
      if (qtyInput) { qtyInput.focus(); qtyInput.select(); }
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' && masterItems.length > 0) {
        e.preventDefault();
        openDrop();
        setHiIdx(0);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHiIdx(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHiIdx(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (hiIdx >= 0 && suggestions[hiIdx]) handleSelect(suggestions[hiIdx]);
        break;
      case 'Escape':
        e.preventDefault();
        closeDrop();
        break;
    }
  }

  // Reposition/close on scroll or resize whenever the dropdown is visible
  useEffect(() => {
    if (!open) return;
    const close = () => closeDrop();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <div ref={wrapRef} className="flex items-center gap-1">
        <input
          value={value}
          onChange={e => { onChange(e.target.value); if (!open && masterItems.length > 0) openDrop(); }}
          onFocus={openDrop}
          onBlur={() => setTimeout(closeDrop, 160)}
          onKeyDown={handleKeyDown}
          placeholder="Service / item name"
          className="flex-1 min-w-0 outline-none text-xs text-gray-800 bg-transparent"
        />
        {masterItems.length > 0 && (
          <ChevronDown size={10} className="text-gray-300 flex-shrink-0"/>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top:      dropPos.top,
            left:     dropPos.left,
            width:    dropPos.width,
            zIndex:   9000,
          }}
          className="bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden max-h-56 overflow-y-auto"
        >
          <div className="px-3 py-1.5 border-b border-gray-100 bg-gray-50">
            <span className="text-2xs font-semibold text-gray-400 uppercase tracking-wider">
              Charge Master — type or use arrows to search
            </span>
          </div>
          {suggestions.map((item, idx) => (
            <button
              key={item.id}
              onMouseDown={e => { e.preventDefault(); handleSelect(item); }}
              className={clsx(
                'w-full text-left px-3 py-2 transition flex items-center justify-between gap-2 border-b border-gray-50 last:border-0',
                idx === hiIdx ? 'bg-navy/10' : 'hover:bg-navy/4',
              )}
            >
              <div className="min-w-0">
                <div className="text-xs font-medium text-navy truncate">{item.service_name}</div>
                {item.doctor_name && (
                  <div className="text-2xs text-gray-400 truncate">Dr. {item.doctor_name}</div>
                )}
              </div>
              <div className="flex-shrink-0 text-right">
                <div className="text-xs font-semibold text-navy">
                  ₹{Number(item.price).toLocaleString('en-IN')}
                </div>
                <div className="text-2xs text-gray-400">
                  {BM_TO_LINE_CAT[item.category] ?? item.category}
                  {item.tax_pct > 0 ? ` · ${item.tax_pct}% GST` : ''}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ── New Invoice Modal ─────────────────────────────────────────────

function NewInvoiceModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const [patient,        setPatient]        = useState<any>(null);
  const [billingType,    setBillingType]    = useState('cash');
  const [tpaName,        setTpaName]        = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [notes,          setNotes]          = useState('');
  const [lines,          setLines]          = useState<LineItem[]>([{ ...BLANK_LINE }]);
  const [error,          setError]          = useState('');
  const [view,         setView]         = useState<'EDIT_ITEMS' | 'RECORD_PAYMENT' | 'SUCCESS'>('EDIT_ITEMS');
  const [createdInvId, setCreatedInvId] = useState<string | null>(null);
  const [newPayAmount, setNewPayAmount] = useState('');
  const [newPayMode,   setNewPayMode]   = useState('cash');
  const [newPayRef,    setNewPayRef]    = useState('');
  const [newPayError,  setNewPayError]  = useState('');
  const [printTarget,  setPrintTarget]  = useState<any>(null);
  const [printSize,    setPrintSize]    = useState<InvoicePaperSize>('A4');
  const savingAsRef = useRef<'draft' | 'payment'>('draft');
  const payModeRef  = useRef<HTMLSelectElement>(null);
  const { data: branding } = useBranding();
  const hospitalName        = useAuthStore(s => s.user?.hospitalName ?? '');

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy';

  // Fetch billing master items once (all categories)
  const { data: masterItems = [] } = useQuery<MasterItem[]>({
    queryKey: ['billing-master-all'],
    queryFn:  () => api.get('/billing-master').then(r => r.data),
    staleTime: 5 * 60_000,
  });

  const totals = useMemo(() => {
    const computed  = lines.map(computeLine);
    const subtotal  = computed.reduce((s, l) => s + l.base, 0);
    const totalDisc = computed.reduce((s, l) => s + l.discAmt, 0) + Number(discountAmount || 0);
    const cgst      = computed.reduce((s, l) => s + l.cgst, 0);
    const sgst      = computed.reduce((s, l) => s + l.sgst, 0);
    const total     = computed.reduce((s, l) => s + l.lineTotal, 0) - Number(discountAmount || 0);
    return { subtotal, totalDisc, cgst, sgst, total: Math.max(0, total) };
  }, [lines, discountAmount]);

  const createMutation = useMutation({
    mutationFn: (payload: any) => api.post('/billing/invoices', payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing-dashboard'] });
      if (savingAsRef.current === 'draft') { onClose(); return; }
      const created = res.data;
      setCreatedInvId(created.id);
      setNewPayAmount(String(Number(created.amount_due ?? created.total_amount ?? 0)));
      setView('RECORD_PAYMENT');
      requestAnimationFrame(() => { if (payModeRef.current) payModeRef.current.focus(); });
    },
    onError: (err: any) => setError(err.response?.data?.message ?? 'Failed to create invoice'),
  });

  const payMutInNew = useMutation({
    mutationFn: () => api.post('/billing/payments', {
      invoice_id:       createdInvId,
      amount:           Number(newPayAmount),
      payment_mode:     newPayMode,
      reference_number: newPayRef || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing-dashboard'] });
      setView('SUCCESS');
    },
    onError: (e: any) => setNewPayError(e.response?.data?.error ?? 'Payment failed'),
  });

  function addLine()              { setLines(l => [...l, { ...BLANK_LINE }]); }
  function removeLine(i: number)  { setLines(l => l.filter((_, idx) => idx !== i)); }
  function updateLine(i: number, key: keyof LineItem, val: any) {
    setLines(l => { const n = [...l]; (n[i] as any)[key] = val; return n; });
  }

  function applyMasterItem(lineIdx: number, item: MasterItem) {
    const cat = BM_TO_LINE_CAT[item.category] ?? 'other';
    setLines(l => {
      const n = [...l];
      n[lineIdx] = {
        ...n[lineIdx],
        description:  item.service_name,
        category:     cat,
        unit_rate:    item.price,
        gst_rate:     item.tax_pct,
        is_exempt:    item.tax_pct === 0,
        hsn_sac_code: ['consultation','nursing','lab','radiology','room_charge'].includes(cat) ? '9993' : '',
      };
      return n;
    });
  }

  function submitCreate() {
    setError('');
    if (!patient)                              { setError('Select a patient'); return; }
    if (lines.some(l => !l.description.trim())){ setError('All line items need a description'); return; }
    createMutation.mutate({
      patient_id:      patient.id,
      billing_type:    billingType,
      line_items:      lines,
      discount_amount: Number(discountAmount || 0) || undefined,
      discount_reason: discountReason || undefined,
      tpa_name:        tpaName || undefined,
      notes:           notes || undefined,
    });
  }
  function handleSaveDraft()        { savingAsRef.current = 'draft';   submitCreate(); }
  function handleProceedToPayment() { savingAsRef.current = 'payment'; submitCreate(); }
  async function handlePrintNewInvoice() {
    if (!createdInvId) return;
    try {
      const res = await api.get(`/billing/invoices/${createdInvId}`);
      setPrintTarget(res.data);
    } catch { /* fail silently — invoice accessible from billing list */ }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-navy"/>
            <h2 className="text-lg font-bold text-navy">New Invoice</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition">
            <X size={18}/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {view === 'EDIT_ITEMS' && (
            <div className="space-y-6">

          {/* Patient */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
              Patient *
            </label>
            {patient ? (
              <div className="flex items-center gap-3 p-3 bg-navy/5 border border-navy/10 rounded-xl">
                <div className="flex-1">
                  <div className="font-semibold text-navy text-sm">{patient.name}</div>
                  <div className="text-xs text-gray-400">
                    {patient.patient_uid}
                    {patient.age_years ? ` · ${patient.age_years}y` : ''}
                    {patient.gender    ? ` · ${patient.gender === 'male' ? 'M' : patient.gender === 'female' ? 'F' : 'O'}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => setPatient(null)}
                  className="text-xs text-gray-400 hover:text-red-500 transition px-2 py-1 rounded-lg hover:bg-red-50">
                  Change
                </button>
              </div>
            ) : (
              <PatientAutocomplete onSelect={p => setPatient(p)}/>
            )}
          </div>

          {/* Billing type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Billing Type
              </label>
              <select value={billingType} onChange={e => setBillingType(e.target.value)} className={inputCls}>
                <option value="cash">Cash</option>
                <option value="tpa">TPA / Insurance</option>
                <option value="pmjay">PM-JAY</option>
                <option value="cghs">CGHS</option>
                <option value="echs">ECHS</option>
                <option value="esi">ESI</option>
              </select>
            </div>
            {billingType !== 'cash' && (
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                  TPA / Scheme Name
                </label>
                <input value={tpaName} onChange={e => setTpaName(e.target.value)}
                  placeholder="e.g. Star Health, ECHS" className={inputCls}/>
              </div>
            )}
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Line Items *
              </label>
              <button onClick={addLine} className="text-xs text-navy hover:underline flex items-center gap-1">
                <Plus size={12}/> Add row
              </button>
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 text-gray-400 font-semibold uppercase w-[28%]">
                      Description {masterItems.length > 0 && <span className="text-orange normal-case font-normal">(type to search)</span>}
                    </th>
                    <th className="text-left px-2 py-2 text-gray-400 font-semibold uppercase w-[16%]">Category</th>
                    <th className="text-right px-2 py-2 text-gray-400 font-semibold uppercase w-[8%]">Qty</th>
                    <th className="text-right px-2 py-2 text-gray-400 font-semibold uppercase w-[11%]">Rate (₹)</th>
                    <th className="text-right px-2 py-2 text-gray-400 font-semibold uppercase w-[8%]">Disc%</th>
                    <th className="text-right px-2 py-2 text-gray-400 font-semibold uppercase w-[10%]">GST%</th>
                    <th className="text-right px-2 py-2 text-gray-400 font-semibold uppercase w-[12%]">Total (₹)</th>
                    <th className="w-[7%]"/>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {lines.map((line, i) => {
                    const c = computeLine(line);
                    return (
                      <tr key={i} className="hover:bg-gray-50/50">
                        <td className="px-3 py-2">
                          <DescriptionCombo
                            value={line.description}
                            lineIndex={i}
                            masterItems={masterItems}
                            onChange={v => updateLine(i, 'description', v)}
                            onSelect={item => applyMasterItem(i, item)}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <select
                            value={line.category}
                            onChange={e => {
                              const cat = e.target.value;
                              updateLine(i, 'category', cat);
                              updateLine(i, 'is_exempt', ['consultation','nursing','diet'].includes(cat));
                            }}
                            className="w-full outline-none text-xs text-gray-600 bg-transparent"
                          >
                            {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" value={line.quantity} min={1}
                            data-line-qty={i}
                            onChange={e => updateLine(i, 'quantity', +e.target.value || 1)}
                            className="w-full outline-none text-xs text-right text-gray-700"/>
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" value={line.unit_rate || ''} min={0}
                            onChange={e => updateLine(i, 'unit_rate', +e.target.value || 0)}
                            placeholder="0"
                            className="w-full outline-none text-xs text-right text-gray-700"/>
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" value={line.discount_pct || ''} min={0} max={100}
                            onChange={e => updateLine(i, 'discount_pct', Math.min(100, +e.target.value || 0))}
                            placeholder="0"
                            className="w-full outline-none text-xs text-right text-gray-700"/>
                        </td>
                        <td className="px-2 py-2">
                          {line.is_exempt ? (
                            <span className="text-xs text-gray-400 block text-right">Exempt</span>
                          ) : (
                            <select value={line.gst_rate}
                              onChange={e => updateLine(i, 'gst_rate', +e.target.value)}
                              className="w-full outline-none text-xs text-right text-gray-600 bg-transparent"
                            >
                              {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                            </select>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right font-semibold text-gray-800">
                          {c.lineTotal.toFixed(2)}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {lines.length > 1 && (
                            <button onClick={() => removeLine(i)}
                              className="text-gray-300 hover:text-red-400 transition">
                              <Trash2 size={12}/>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Discount + Notes */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Additional Discount (₹)
              </label>
              <input type="number" value={discountAmount}
                onChange={e => setDiscountAmount(e.target.value)}
                placeholder="0" min={0} className={inputCls}/>
              <p className="text-xs text-gray-400 mt-1">&gt;₹1,000 or &gt;10% requires admin approval</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Discount Reason
              </label>
              <input value={discountReason} onChange={e => setDiscountReason(e.target.value)}
                placeholder="Staff benefit, BPL, etc." className={inputCls}/>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                placeholder="Internal notes…"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-navy"/>
            </div>
          </div>

          {/* Totals */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{fmt(totals.subtotal)}</span></div>
            {totals.totalDisc > 0 && (
              <div className="flex justify-between text-red-500"><span>Discount</span><span>-{fmt(totals.totalDisc)}</span></div>
            )}
            {totals.cgst > 0 && (
              <>
                <div className="flex justify-between text-gray-500"><span>CGST</span><span>{fmt(totals.cgst)}</span></div>
                <div className="flex justify-between text-gray-500"><span>SGST</span><span>{fmt(totals.sgst)}</span></div>
              </>
            )}
            <div className="flex justify-between font-bold text-navy text-base pt-2 border-t border-gray-200">
              <span>Total</span><span>{fmt(totals.total)}</span>
            </div>
          </div>

          {error && <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
            </div>
          )}

          {view === 'RECORD_PAYMENT' && (
            <div className="space-y-5 py-2">
              <div className="bg-navy/5 border border-navy/10 rounded-xl p-4">
                <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Invoice Summary</div>
                <div className="font-semibold text-navy">{patient?.name}</div>
                <div className="text-sm text-gray-500 mt-0.5">
                  Total: <span className="font-bold text-navy">{fmt(totals.total)}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1.5">Amount (₹) *</label>
                  <input
                    type="number"
                    value={newPayAmount}
                    onChange={e => setNewPayAmount(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1.5">Mode *</label>
                  <select
                    ref={payModeRef}
                    value={newPayMode}
                    onChange={e => setNewPayMode(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"
                  >
                    {['cash','upi','card','neft','cheque','dd','wallet'].map(m => (
                      <option key={m} value={m}>{m.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1.5">
                    Reference # (optional)
                  </label>
                  <input
                    value={newPayRef}
                    onChange={e => setNewPayRef(e.target.value)}
                    placeholder="UPI transaction ID, cheque number…"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"
                  />
                </div>
              </div>
              {newPayError && (
                <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 rounded-lg px-3 py-2">
                  <AlertCircle size={14}/> {newPayError}
                </div>
              )}
            </div>
          )}

          {view === 'SUCCESS' && (
            <div className="py-12 text-center space-y-4">
              <CheckCircle2 size={48} className="text-green-500 mx-auto"/>
              <div>
                <p className="text-xl font-bold text-navy">Payment Recorded</p>
                <p className="text-sm text-gray-500 mt-1">
                  {fmt(Number(newPayAmount))} received &mdash; {patient?.name}
                </p>
              </div>
            </div>
          )}
        </div>

        {view === 'EDIT_ITEMS' && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50">
            <button onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white transition">
              Cancel
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveDraft}
                disabled={createMutation.isPending || !patient}
                className="px-4 py-2 rounded-xl border border-navy text-navy text-sm font-medium hover:bg-navy/5 disabled:opacity-50 transition"
              >
                {createMutation.isPending && savingAsRef.current === 'draft' ? 'Saving…' : 'Save Draft'}
              </button>
              <button
                onClick={handleProceedToPayment}
                disabled={createMutation.isPending || !patient}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition"
              >
                <CreditCard size={14}/>
                {createMutation.isPending && savingAsRef.current === 'payment'
                  ? 'Saving…'
                  : `Proceed to Payment · ${fmt(totals.total)}`}
              </button>
            </div>
          </div>
        )}
        {view === 'RECORD_PAYMENT' && (
          <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50">
            <button
              onClick={() => setView('EDIT_ITEMS')}
              disabled={payMutInNew.isPending}
              className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white disabled:opacity-50 transition"
            >
              Back to Items
            </button>
            <div className="flex-1"/>
            <button
              onClick={() => { setNewPayError(''); payMutInNew.mutate(); }}
              disabled={!newPayAmount || Number(newPayAmount) <= 0 || payMutInNew.isPending}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition"
            >
              {payMutInNew.isPending
                ? <><Loader2 size={14} className="animate-spin"/> Recording…</>
                : <><CreditCard size={14}/> Record {newPayAmount ? fmt(Number(newPayAmount)) : 'Payment'}</>}
            </button>
          </div>
        )}
        {view === 'SUCCESS' && (
          <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50">
            <button
              onClick={handlePrintNewInvoice}
              disabled={!branding}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy/90 disabled:opacity-50 transition"
            >
              <Printer size={14}/> Print Receipt
            </button>
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white transition">
              Close
            </button>
          </div>
        )}
      </div>

      {printTarget && branding && (
        <PrintableInvoice
          inv={printTarget}
          branding={branding}
          hospitalName={hospitalName}
          paperSize={printSize}
          onDone={() => setPrintTarget(null)}
        />
      )}
    </div>
  );
}

// ── Invoice Detail Modal ──────────────────────────────────────────

function InvoiceDetailModal({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const qc = useQueryClient();

  // ── View-mode action state ────────────────────────────────────────
  const [cancelMode,    setCancelMode]    = useState(false);
  const [cancelReason,  setCancelReason]  = useState('');
  const [payMode,       setPayMode]       = useState(false);
  const [payAmount,     setPayAmount]     = useState('');
  const [paymentMode,   setPaymentMode]   = useState('cash');
  const [payRef,        setPayRef]        = useState('');
  const [actionError,   setActionError]   = useState('');
  const [deletingIdx,   setDeletingIdx]   = useState<number | null>(null);
  const [paySuccess,    setPaySuccess]    = useState(false);
  const payModeSelectRef = useRef<HTMLSelectElement>(null);

  // ── Edit-mode state ───────────────────────────────────────────────
  const [editMode,           setEditMode]           = useState(false);
  const [editLines,          setEditLines]          = useState<LineItem[]>([]);
  const [editDiscount,       setEditDiscount]       = useState('');
  const [editDiscountReason, setEditDiscountReason] = useState('');
  const [editNotes,          setEditNotes]          = useState('');

  const { data: branding }  = useBranding();
  const hospitalName         = useAuthStore(s => s.user?.hospitalName ?? '');
  const [printSize,   setPrintSize]   = useState<InvoicePaperSize>('A4');
  const [printTarget, setPrintTarget] = useState<any>(null);

  const { data: inv, isLoading } = useQuery({
    queryKey: ['invoice-detail', invoiceId],
    queryFn:  () => api.get(`/billing/invoices/${invoiceId}`).then(r => r.data),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['invoice-detail', invoiceId] });
    qc.invalidateQueries({ queryKey: ['invoices'] });
    qc.invalidateQueries({ queryKey: ['billing-dashboard'] });
  }

  const finalizeMut = useMutation({
    mutationFn: () => api.post(`/billing/invoices/${invoiceId}/finalize`),
    onSuccess: invalidate,
    onError: (e: any) => setActionError(e.response?.data?.error ?? 'Failed to finalize'),
  });

  const cancelMut = useMutation({
    mutationFn: () => api.post(`/billing/invoices/${invoiceId}/cancel`, { reason: cancelReason }),
    onSuccess: () => { invalidate(); setCancelMode(false); },
    onError: (e: any) => setActionError(e.response?.data?.error ?? 'Failed to cancel'),
  });

  const payMut = useMutation({
    mutationFn: () => api.post('/billing/payments', {
      invoice_id:       invoiceId,
      amount:           Number(payAmount),
      payment_mode:     paymentMode,
      reference_number: payRef || undefined,
    }),
    onSuccess: () => { invalidate(); setPaySuccess(true); },
    onError: (e: any) => setActionError(e.response?.data?.error ?? 'Failed to record payment'),
  });

  const deleteItemMut = useMutation({
    mutationFn: (idx: number) => api.delete(`/billing/invoices/${invoiceId}/items/${idx}`),
    onSuccess: (res) => {
      const updated = res.data;
      qc.setQueryData(['invoice-detail', invoiceId], (old: any) => {
        if (!old) return old;
        const newSubtotal = (updated.line_items as any[]).reduce(
          (s: number, l: any) => s + Number(l.base_amount ?? 0), 0
        );
        return {
          ...old,
          line_items:   updated.line_items,
          subtotal:     newSubtotal,
          total_amount: updated.total_amount,
          amount_due:   updated.amount_due,
          status:       updated.new_status,
        };
      });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['billing-dashboard'] });
      setDeletingIdx(null);
      setActionError('');
    },
    onError: (e: any) => {
      setDeletingIdx(null);
      setActionError(e.response?.data?.error ?? 'Failed to remove item');
    },
  });

  // ── Charge master (for edit-mode combo) ──────────────────────────
  const { data: masterItems = [] } = useQuery<MasterItem[]>({
    queryKey: ['billing-master-all'],
    queryFn:  () => api.get('/billing-master').then(r => r.data),
    staleTime: 5 * 60_000,
    enabled:  editMode,
  });

  // Live totals while editing
  const editTotals = useMemo(() => {
    if (!editMode) return null;
    const computed  = editLines.map(computeLine);
    const subtotal  = computed.reduce((s, l) => s + l.base, 0);
    const discount  = Number(editDiscount || 0);
    const cgst      = computed.reduce((s, l) => s + l.cgst, 0);
    const sgst      = computed.reduce((s, l) => s + l.sgst, 0);
    const total     = Math.max(0, computed.reduce((s, l) => s + l.lineTotal, 0) - discount);
    return { subtotal, discount, cgst, sgst, total };
  }, [editMode, editLines, editDiscount]);

  // Save edits mutation
  const updateMut = useMutation({
    mutationFn: () => api.patch(`/billing/invoices/${invoiceId}`, {
      line_items:      editLines.map(l => ({
        ...l,
        hsn_sac_code: l.hsn_sac_code || undefined,
      })),
      discount_amount: Number(editDiscount) || 0,
      discount_reason: editDiscountReason || undefined,
      notes:           editNotes || undefined,
    }),
    onSuccess: () => { invalidate(); setEditMode(false); setActionError(''); },
    onError:   (e: any) => {
      const data = e.response?.data;
      const msg  = data?.error ?? data?.message ?? e.message ?? 'Failed to save changes';
      setActionError(msg);
    },
  });

  // Enter / exit edit mode
  function enterEditMode() {
    if (!inv) return;
    setEditLines((inv.line_items ?? []).map((l: any): LineItem => ({
      description:  l.description  ?? '',
      category:     l.category     ?? 'other',
      hsn_sac_code: l.hsn_sac_code ?? '',
      quantity:     Number(l.quantity    ?? 1),
      unit_rate:    Number(l.unit_rate   ?? 0),
      discount_pct: Number(l.discount_pct ?? 0),
      gst_rate:     Number(l.gst_rate    ?? 0),
      is_exempt:    Boolean(l.is_exempt  ?? true),
    })));
    setEditDiscount(Number(inv.discount_amount) > 0 ? String(inv.discount_amount) : '');
    setEditDiscountReason(inv.discount_reason ?? '');
    setEditNotes(inv.notes ?? '');
    setCancelMode(false);
    setPayMode(false);
    setActionError('');
    setEditMode(true);
  }

  function exitEditMode() { setEditMode(false); setActionError(''); }

  function addEditLine()              { setEditLines(l => [...l, { ...BLANK_LINE }]); }
  function removeEditLine(i: number)  { setEditLines(l => l.filter((_, idx) => idx !== i)); }
  function updateEditLine(i: number, key: keyof LineItem, val: any) {
    setEditLines(l => { const n = [...l]; (n[i] as any)[key] = val; return n; });
  }
  function applyMasterToEdit(lineIdx: number, item: MasterItem) {
    const cat = BM_TO_LINE_CAT[item.category] ?? 'other';
    setEditLines(l => {
      const n = [...l];
      n[lineIdx] = {
        ...n[lineIdx],
        description:  item.service_name,
        category:     cat,
        unit_rate:    item.price,
        gst_rate:     item.tax_pct,
        is_exempt:    item.tax_pct === 0,
        hsn_sac_code: ['consultation','nursing','lab','radiology','room_charge'].includes(cat) ? '9993' : '',
      };
      return n;
    });
  }

  // Print is handled via PrintableInvoice portal — set printTarget to trigger it

  const canFinalize = inv?.status === 'draft';
  const canPay      = ['draft','final','partially_paid'].includes(inv?.status ?? '');
  const canCancel   = inv?.status !== 'cancelled' && Number(inv?.amount_paid ?? 0) === 0;
  const canEdit     = inv?.status !== 'paid' && inv?.status !== 'cancelled';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:rounded-2xl shadow-2xl sm:max-w-2xl max-h-[95vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <FileText size={18} className="text-navy"/>
            <div>
              <div className="font-bold text-navy font-mono text-sm">
                {isLoading ? '…' : inv?.invoice_number}
              </div>
              {inv && (
                <div className="text-xs text-gray-400">
                  {new Date(inv.invoice_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                  &nbsp;·&nbsp;
                  <span className={clsx('font-medium', STATUS_STYLE[inv.status] ?? '')}>
                    {inv.status.replace('_',' ').toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X size={18}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 size={24} className="animate-spin"/>
            </div>
          ) : !inv ? (
            <div className="text-center py-12 text-gray-400">Invoice not found</div>
          ) : editMode ? (

            /* ════════════════ EDIT MODE ════════════════ */
            <>
              {/* Edit mode banner */}
              <div className="flex items-center gap-2 bg-orange/10 border border-orange/20 rounded-xl px-3 py-2">
                <span className="text-xs font-semibold text-orange uppercase tracking-wide">Editing Invoice</span>
                <span className="text-xs text-gray-500 ml-1">Changes are saved when you click Save Changes.</span>
              </div>

              {/* Editable line items table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Line Items</span>
                  <button onClick={addEditLine}
                    className="flex items-center gap-1 text-xs text-navy hover:underline font-medium">
                    <Plus size={11}/> Add Row
                  </button>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-3 py-2 text-gray-400 font-semibold uppercase w-[36%]">
                        Description {masterItems.length > 0 && <span className="text-orange normal-case font-normal">(type to search)</span>}
                      </th>
                      <th className="text-left px-2 py-2 text-gray-400 font-semibold uppercase w-[14%]">Category</th>
                      <th className="text-right px-2 py-2 text-gray-400 font-semibold uppercase w-[8%]">Qty</th>
                      <th className="text-right px-2 py-2 text-gray-400 font-semibold uppercase w-[12%]">Rate (₹)</th>
                      <th className="text-right px-2 py-2 text-gray-400 font-semibold uppercase w-[10%]">Disc%</th>
                      <th className="text-right px-2 py-2 text-gray-400 font-semibold uppercase w-[12%]">GST</th>
                      <th className="text-right px-2 py-2 text-gray-400 font-semibold uppercase w-[10%]">Total</th>
                      <th className="w-8"/>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {editLines.map((line, i) => {
                      const c = computeLine(line);
                      return (
                        <tr key={i} className="hover:bg-gray-50/50">
                          <td className="px-3 py-2">
                            <DescriptionCombo
                              value={line.description}
                              lineIndex={i}
                              masterItems={masterItems}
                              onChange={v => updateEditLine(i, 'description', v)}
                              onSelect={item => applyMasterToEdit(i, item)}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <select value={line.category}
                              onChange={e => { const cat = e.target.value; updateEditLine(i,'category',cat); updateEditLine(i,'is_exempt',['consultation','nursing','diet'].includes(cat)); }}
                              className="w-full outline-none text-xs text-gray-600 bg-transparent">
                              {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" value={line.quantity} min={1}
                              data-line-qty={i}
                              onChange={e => updateEditLine(i,'quantity',+e.target.value || 1)}
                              className="w-full outline-none text-xs text-right text-gray-700 bg-transparent"/>
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" value={line.unit_rate || ''} min={0}
                              onChange={e => updateEditLine(i,'unit_rate',+e.target.value || 0)}
                              placeholder="0"
                              className="w-full outline-none text-xs text-right text-gray-700 bg-transparent"/>
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" value={line.discount_pct || ''} min={0} max={100}
                              onChange={e => updateEditLine(i,'discount_pct',Math.min(100,+e.target.value||0))}
                              placeholder="0"
                              className="w-full outline-none text-xs text-right text-gray-700 bg-transparent"/>
                          </td>
                          <td className="px-2 py-2">
                            {line.is_exempt ? (
                              <span className="text-xs text-gray-400 block text-right">Exempt</span>
                            ) : (
                              <select value={line.gst_rate}
                                onChange={e => updateEditLine(i,'gst_rate',+e.target.value)}
                                className="w-full outline-none text-xs text-right text-gray-600 bg-transparent">
                                {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                              </select>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right font-semibold text-gray-800">
                            {c.lineTotal.toFixed(2)}
                          </td>
                          <td className="px-2 py-2 text-center">
                            {editLines.length > 1 && (
                              <button onClick={() => removeEditLine(i)}
                                className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                                <Trash2 size={11}/>
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Discount + Notes */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Additional Discount (₹)</label>
                  <input type="number" value={editDiscount}
                    onChange={e => setEditDiscount(e.target.value)}
                    placeholder="0" min={0}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Discount Reason</label>
                  <input value={editDiscountReason} onChange={e => setEditDiscountReason(e.target.value)}
                    placeholder="e.g. Staff benefit, BPL"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"/>
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Notes</label>
                  <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={2}
                    placeholder="Internal notes…"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-navy"/>
                </div>
              </div>

              {/* Live totals preview */}
              {editTotals && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-1.5 text-sm">
                  <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{fmt(editTotals.subtotal)}</span></div>
                  {editTotals.discount > 0 && (
                    <div className="flex justify-between text-red-500"><span>Discount</span><span>-{fmt(editTotals.discount)}</span></div>
                  )}
                  {editTotals.cgst > 0 && (<>
                    <div className="flex justify-between text-gray-500"><span>CGST</span><span>{fmt(editTotals.cgst)}</span></div>
                    <div className="flex justify-between text-gray-500"><span>SGST</span><span>{fmt(editTotals.sgst)}</span></div>
                  </>)}
                  <div className="flex justify-between font-bold text-navy text-base border-t border-gray-200 pt-2">
                    <span>New Total</span><span>{fmt(editTotals.total)}</span>
                  </div>
                </div>
              )}

              {actionError && (
                <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 rounded-lg px-3 py-2">
                  <AlertCircle size={14}/> {actionError}
                </div>
              )}
            </>

          ) : (

            /* ════════════════ VIEW MODE ════════════════ */
            <>
              {/* Patient */}
              <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-navy">{inv.patient_name ?? inv.patient_uid}</div>
                  <div className="text-xs text-gray-400">
                    {inv.patient_uid}
                    {inv.age_years  ? ` · ${inv.age_years}y` : ''}
                    {inv.gender     ? ` · ${inv.gender === 'male' ? 'M' : inv.gender === 'female' ? 'F' : 'O'}` : ''}
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600">
                  {BILLING_TYPE_LABEL[inv.billing_type] ?? inv.billing_type}
                </span>
              </div>

              {/* Line items */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-3 py-2 text-gray-400 font-semibold uppercase">Description</th>
                      <th className="text-right px-3 py-2 text-gray-400 font-semibold uppercase">Qty</th>
                      <th className="text-right px-3 py-2 text-gray-400 font-semibold uppercase">Rate</th>
                      <th className="text-right px-3 py-2 text-gray-400 font-semibold uppercase">GST</th>
                      <th className="text-right px-3 py-2 text-gray-400 font-semibold uppercase">Total</th>
                      {canEdit && (inv.line_items ?? []).length > 1 && <th className="w-8"/>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(inv.line_items ?? []).map((l: any, i: number) => {
                      const isDeleting = deletingIdx === i;
                      return (
                        <tr key={i} className={clsx('transition-colors', isDeleting ? 'bg-red-50/60' : 'hover:bg-gray-50/50')}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-800">{l.description}</div>
                            <div className="text-gray-400 capitalize">{l.category?.replace('_',' ')}</div>
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700">{l.quantity}</td>
                          <td className="px-3 py-2 text-right text-gray-700">₹{Number(l.unit_rate).toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 text-right text-gray-400">
                            {l.is_exempt ? 'Exempt' : `${l.gst_rate}%`}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-800">
                            ₹{Number(l.line_total).toLocaleString('en-IN')}
                          </td>
                          {canEdit && (inv.line_items ?? []).length > 1 && (
                            <td className="px-2 py-2 text-center">
                              <button
                                onClick={() => { setActionError(''); setDeletingIdx(i); deleteItemMut.mutate(i); }}
                                disabled={deletingIdx !== null}
                                title="Remove this item"
                                className={clsx('p-1 rounded transition-colors',
                                  isDeleting
                                    ? 'text-red-400 cursor-not-allowed'
                                    : 'text-gray-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed',
                                )}>
                                {isDeleting ? <Loader2 size={12} className="animate-spin"/> : <Minus size={12}/>}
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 text-sm">
                <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{fmt(inv.subtotal)}</span></div>
                {Number(inv.discount_amount) > 0 && (
                  <div className="flex justify-between text-red-500">
                    <span>Discount {inv.discount_reason ? `(${inv.discount_reason})` : ''}</span>
                    <span>-{fmt(inv.discount_amount)}</span>
                  </div>
                )}
                {Number(inv.cgst_amount) > 0 && (<>
                  <div className="flex justify-between text-gray-500"><span>CGST</span><span>{fmt(inv.cgst_amount)}</span></div>
                  <div className="flex justify-between text-gray-500"><span>SGST</span><span>{fmt(inv.sgst_amount)}</span></div>
                </>)}
                <div className="flex justify-between font-bold text-navy text-base border-t border-gray-200 pt-2">
                  <span>Total</span><span>{fmt(inv.total_amount)}</span>
                </div>
                {Number(inv.amount_paid) > 0 && (
                  <div className="flex justify-between text-green-600 font-medium">
                    <span>Paid</span><span>{fmt(inv.amount_paid)}</span>
                  </div>
                )}
                {Number(inv.amount_due) > 0 && (
                  <div className="flex justify-between text-red-500 font-bold">
                    <span>Balance Due</span><span>{fmt(inv.amount_due)}</span>
                  </div>
                )}
              </div>

              {/* Payment history */}
              {(inv.payments ?? []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-gray-400 uppercase mb-2">Payments</div>
                  <div className="space-y-1">
                    {inv.payments.map((p: any) => (
                      <div key={p.id} className="flex justify-between text-xs text-gray-600 bg-green-50 rounded-lg px-3 py-2">
                        <span className="capitalize">{p.payment_mode} {p.reference_number ? `(${p.reference_number})` : ''}</span>
                        <span className="font-semibold text-green-700">+{fmt(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {inv.notes && (
                <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">{inv.notes}</p>
              )}

              {/* Cancel form */}
              {cancelMode && (
                <div className="border border-red-200 bg-red-50 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-red-700">Cancel Invoice</p>
                  <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                    placeholder="Reason for cancellation (required)" rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-red-200 text-sm focus:outline-none resize-none"/>
                  <div className="flex gap-2">
                    <button onClick={() => { setCancelMode(false); setActionError(''); }}
                      className="flex-1 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white">Back</button>
                    <button onClick={() => { setActionError(''); cancelMut.mutate(); }}
                      disabled={!cancelReason.trim() || cancelMut.isPending}
                      className="flex-1 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                      {cancelMut.isPending ? <><Loader2 size={13} className="animate-spin"/> Cancelling…</> : 'Confirm Cancel'}
                    </button>
                  </div>
                </div>
              )}

              {/* Record Payment - success state */}
              {paySuccess && (
                <div className="border border-green-200 bg-green-50 rounded-xl p-5 text-center space-y-3">
                  <CheckCircle2 size={36} className="text-green-500 mx-auto"/>
                  <p className="font-semibold text-green-800">Payment Recorded</p>
                  <p className="text-sm text-gray-500">
                    {fmt(Number(payAmount))} received
                    {Number(inv?.amount_due ?? 0) > 0.01
                      ? ` · Balance: ${fmt(inv?.amount_due)}`
                      : ' · Invoice fully settled'}
                  </p>
                </div>
              )}

              {/* Record Payment form */}
              {payMode && !paySuccess && (
                <div className="border border-green-200 bg-green-50 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-green-800">Record Payment</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Amount (₹) *</label>
                      <input type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                        placeholder={fmt(inv.amount_due)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-navy"/>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Mode *</label>
                      <select ref={payModeSelectRef} value={paymentMode} onChange={e => setPaymentMode(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-navy">
                        {['cash','upi','card','neft','cheque','dd','wallet'].map(m => (
                          <option key={m} value={m}>{m.toUpperCase()}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500 block mb-1">Reference # (optional)</label>
                      <input value={payRef} onChange={e => setPayRef(e.target.value)}
                        placeholder="UPI transaction ID, cheque number…"
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-navy"/>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setPayMode(false); setActionError(''); }}
                      className="flex-1 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white">Back</button>
                    <button onClick={() => { setActionError(''); payMut.mutate(); }}
                      disabled={!payAmount || Number(payAmount) <= 0 || payMut.isPending}
                      className="flex-1 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                      {payMut.isPending ? <><Loader2 size={13} className="animate-spin"/> Recording…</> : `Record ${payAmount ? fmt(Number(payAmount)) : ''}`}
                    </button>
                  </div>
                </div>
              )}

              {actionError && (
                <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 rounded-lg px-3 py-2">
                  <AlertCircle size={14}/> {actionError}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {inv && (
          editMode ? (
            /* Edit mode footer — Save / Discard */
            <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50">
              <button onClick={exitEditMode} disabled={updateMut.isPending}
                className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white disabled:opacity-50">
                Discard
              </button>
              <div className="flex-1"/>
              <button
                onClick={() => { setActionError(''); updateMut.mutate(); }}
                disabled={editLines.length === 0 || editLines.some(l => !l.description.trim()) || updateMut.isPending}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy/90 disabled:opacity-50">
                {updateMut.isPending
                  ? <><Loader2 size={13} className="animate-spin"/> Saving…</>
                  : <><FileText size={13}/> Save Changes {editTotals ? `· ${fmt(editTotals.total)}` : ''}</>}
              </button>
            </div>
          ) : paySuccess ? (
            /* Payment success footer */
            <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50">
              <button
                onClick={() => setPrintTarget(inv)}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy/90"
              >
                <Printer size={14}/> Print Receipt
              </button>
              <button
                onClick={() => { setPaySuccess(false); setPayMode(false); setPayAmount(''); setPayRef(''); }}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-white"
              >
                Close
              </button>
            </div>
          ) : (
            /* View mode footer */
            !cancelMode && !payMode && (
              <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50 flex-wrap">
                <div className="flex items-stretch rounded-xl border border-gray-200 overflow-hidden bg-white">
                  <button
                    onClick={() => inv && setPrintTarget(inv)}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 border-r border-gray-200"
                  >
                    <Printer size={14}/> Print
                  </button>
                  <select
                    value={printSize}
                    onChange={e => setPrintSize(e.target.value as InvoicePaperSize)}
                    className="text-xs text-gray-500 px-2 bg-transparent focus:outline-none cursor-pointer"
                    title="Paper size"
                  >
                    <option value="A4">A4</option>
                    <option value="A5">A5</option>
                    <option value="THERMAL_80MM">Thermal 80mm</option>
                  </select>
                </div>
                {canEdit && (
                  <button onClick={enterEditMode}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-orange/30 text-sm text-orange hover:bg-orange/5">
                    <Plus size={14}/> Edit Items
                  </button>
                )}
                {canCancel && (
                  <button onClick={() => { setCancelMode(true); setActionError(''); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-200 text-sm text-red-600 hover:bg-red-50">
                    <Ban size={14}/> Cancel Invoice
                  </button>
                )}
                <div className="flex-1"/>
                {canFinalize && (
                  <button onClick={() => { setActionError(''); finalizeMut.mutate(); }} disabled={finalizeMut.isPending}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy/90 disabled:opacity-50">
                    {finalizeMut.isPending ? <><Loader2 size={13} className="animate-spin"/> Finalizing…</> : <><ChevronRight size={14}/> Finalize Invoice</>}
                  </button>
                )}
                {canPay && (
                  <button
                    onClick={() => {
                      setPayMode(true);
                      setActionError('');
                      setPayAmount(String(inv.amount_due));
                      requestAnimationFrame(() => { if (payModeSelectRef.current) payModeSelectRef.current.focus(); });
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700">
                    <CreditCard size={14}/> Record Payment
                  </button>
                )}
              </div>
            )
          )
        )}
      </div>

      {/* Print portal — renders into #as-print-portal, visible only @media print */}
      {printTarget && branding && (
        <PrintableInvoice
          inv={printTarget}
          branding={branding}
          hospitalName={hospitalName}
          paperSize={printSize}
          onDone={() => setPrintTarget(null)}
        />
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────

export default function BillingPage() {
  const user        = useAuthStore(s => s.user);
  const canWrite    = BILLING_WRITE_ROLES.has(user?.role ?? '');
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters,    setFilters]    = useState({ status: '', billing_type: '', page: 1 });
  const [showNew,    setShowNew]    = useState(false);
  const [detailId,   setDetailId]   = useState<string | null>(null);
  const today = format(new Date(), 'yyyy-MM-dd');

  // Deep-link: ?invoice=INV-2026-000004 → resolve UUID then open detail modal
  const deepLinkInvoice = searchParams.get('invoice');
  useEffect(() => {
    if (!deepLinkInvoice) return;
    api.get('/billing/invoices/lookup', { params: { invoice_number: deepLinkInvoice } })
      .then(r => {
        if (r.data?.id) setDetailId(r.data.id);
      })
      .catch(() => { /* invoice not found or no permission — fail silently */ })
      .finally(() => setSearchParams(p => { p.delete('invoice'); return p; }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkInvoice]);

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', filters],
    queryFn:  () => api.get('/billing/invoices', { params: { ...filters, from_date: today, to_date: today } }).then(r => r.data),
    staleTime: 30_000,
  });

  const { data: dashboard } = useQuery({
    queryKey: ['billing-dashboard'],
    queryFn:  () => api.get('/billing/dashboard').then(r => r.data),
    staleTime: 60_000,
  });

  const invoices = data?.invoices ?? [];
  const kpi      = dashboard?.today ?? {};
  const kpiMonth = dashboard?.month ?? {};

  return (
    <div className="space-y-6 p-6 max-w-screen-xl mx-auto">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">Billing</h1>
          <p className="text-sm text-gray-400 mt-0.5">GST-compliant invoicing & payments</p>
        </div>
        {canWrite && (
          <button
            onClick={() => setShowNew(true)}
            className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-xl text-sm hover:bg-navy/90 transition"
          >
            <Plus size={16}/> New Invoice
          </button>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Today's Revenue"  value={fmt(kpi.revenue_today)}     icon={IndianRupee} accent="bg-green-100 text-green-600"/>
        <KpiCard label="Collected Today"  value={fmt(kpi.collected_today)}   icon={CheckCircle2} accent="bg-blue-100 text-blue-600"/>
        <KpiCard label="Outstanding"      value={fmt(dashboard?.outstanding)} icon={Clock}       accent="bg-yellow-100 text-yellow-600"/>
        <KpiCard label="Month Revenue"    value={fmt(kpiMonth.revenue_month)} icon={TrendingUp}  accent="bg-purple-100 text-purple-600"/>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select
          value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="final">Final</option>
          <option value="paid">Paid</option>
          <option value="partially_paid">Partially Paid</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          value={filters.billing_type}
          onChange={e => setFilters(f => ({ ...f, billing_type: e.target.value, page: 1 }))}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"
        >
          <option value="">All Types</option>
          {Object.entries(BILLING_TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Invoice table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              {['Invoice #','Patient','Type','Total','Due','Status','Date'].map(h => (
                <th key={h} className={clsx(
                  'px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide',
                  ['Total','Due'].includes(h) ? 'text-right' : 'text-left',
                )}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {isLoading ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading…</td></tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-12">
                  <FileText size={36} className="mx-auto text-gray-300 mb-3"/>
                  <p className="text-gray-400 text-sm">No invoices found</p>
                </td>
              </tr>
            ) : invoices.map((inv: any) => (
              <tr key={inv.id}
                onClick={() => setDetailId(inv.id)}
                className="hover:bg-orange/4 cursor-pointer transition">
                <td className="px-4 py-3 font-mono text-xs text-blue-600">{inv.invoice_number}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800">{inv.patient_name ?? inv.patient_uid}</div>
                  <div className="text-xs text-gray-400">{inv.patient_uid}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                    {BILLING_TYPE_LABEL[inv.billing_type] ?? inv.billing_type}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-gray-800">{fmt(inv.total_amount)}</td>
                <td className={clsx('px-4 py-3 text-right font-semibold',
                  Number(inv.amount_due) > 0 ? 'text-red-500' : 'text-green-600',
                )}>
                  {fmt(inv.amount_due)}
                </td>
                <td className="px-4 py-3">
                  <span className={clsx('px-2 py-1 rounded-lg text-xs font-medium', STATUS_STYLE[inv.status])}>
                    {inv.status.replace('_', ' ').toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">
                  {new Date(inv.invoice_date).toLocaleDateString('en-IN')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew   && <NewInvoiceModal onClose={() => setShowNew(false)}/>}
      {detailId  && <InvoiceDetailModal invoiceId={detailId} onClose={() => setDetailId(null)}/>}
    </div>
  );
}
